import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box, Typography, TextField, Grid, Paper, Dialog, DialogTitle, DialogContent, 
  DialogActions, Autocomplete, Chip, Alert, IconButton, List, ListItem, 
  ListItemText, ListItemSecondaryAction, Button, Tab, CircularProgress, Divider,
  Card, CardContent, CardHeader, Avatar, Checkbox, FormControlLabel, InputLabel, 
  InputAdornment, ListItemButton, MenuItem, ListItemIcon, Tooltip
} from '@mui/material';

// ICONOS (Asegúrate de que todos terminen en "Icon")
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import MedicationIcon from '@mui/icons-material/Medication';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'; 
import BiotechIcon from '@mui/icons-material/Biotech'; 
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import WhatsAppIcon from '@mui/icons-material/WhatsApp'; 
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import PostAddIcon from '@mui/icons-material/PostAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BlockIcon from '@mui/icons-material/Block';
import LockIcon from '@mui/icons-material/Lock';

import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { nanoid } from 'nanoid';

// AQUÍ RECIÉN PUEDE IR CÓDIGO EJECUTABLE
dayjs.locale('es'); 

// ================= HELPERS =================
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('token');
  return fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  });
};

// ================= COMPONENT =================

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

function MedicalHistoryPage() {
  const { patientId } = useParams(); 
  const navigate = useNavigate();
  const { user } = useAuth(); 
  const isAdmin = user?.role === 'administrador';
  
  // --- ESTADOS DE DATOS ---
  const [patient, setPatient] = useState(null);
  const [activePendings, setActivePendings] = useState([]); 
  const [newPendingText, setNewPendingText] = useState('');
  const [newBackgroundMed, setNewBackgroundMed] = useState('');
  const [availablePrices, setAvailablePrices] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  
  const [unifiedHistory, setUnifiedHistory] = useState([]);
  const [evolutionsList, setEvolutionsList] = useState([]); 

  // --- ESTADOS PARA MACROS (PLANTILLAS) ---
  const [macros, setMacros] = useState([]); 
  const [macroDialog, setMacroDialog] = useState({ open: false, targetField: null });
  const [isCreatingMacro, setIsCreatingMacro] = useState(false); // Nuevo: Modo creación
  const [newMacroForm, setNewMacroForm] = useState({ title: '', content: '' }); // Nuevo: Formulario macro

  // ESTADO DE EVOLUCIÓN (CON SIGNOS VITALES)
  const [newEvolution, setNewEvolution] = useState({
    date: dayjs(), 
    evolutionText: '', 
	consultationReason: '',
    treatmentAndConduct: '', 
    weight: '',
    height: '',
    bmi: '',
    systolic: '',
    diastolic: '',
    heartRate: '',
    temperature: '',
	visitType: 'SUCESIVO', // Por defecto sucesivo (asumiendo que si ya tiene historia, es control)
	amountPaid: 0,
	servicePriceId: '',
	specialty: '',
  });

  const [medicalHistory, setMedicalHistory] = useState({
    allergies: '',
    background: { 
        previousPathologies: '', surgical: '', hospitalizations: '', family: { parents: '', children: '' }, 
        currentMedications: [], 
        habits: { tabaquico: { active: false, notes: '' }, alcoholico: { active: false, notes: '' }, cafeinico: { active: false, notes: '' }, medicamentoso: { active: false, notes: '' } },
        otherBackground: { childrenCount: 0, hereditaryConditions: '', sexualPartner: false, sexualPartnerNotes: '' }
    },
    gynecoObstetric: { menarche: { age: '' }, sexarchy: { age: '' }, menopause: '', gestas: '', lastMenstruation: null, cycleDescription: '' },
    currentIllness: { description: '', diagnoses: [] }, 
    currentIllnessHistory: [],
    physicalExam: { description: '', paraclinicals: '' },
    physicalExamHistory: [],
    treatment: { treatmentHistory: [] }
  });

  const [tabValue, setTabValue] = useState('1');
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Estados auxiliares
  const [openDiagDialog, setOpenDiagDialog] = useState(false);
  const [selectedDiag, setSelectedDiag] = useState(null); 
  const [manualDiag, setManualDiag] = useState('');
  const [cieOptions, setCieOptions] = useState([]); 
  const [cieLoading, setCieLoading] = useState(false); 
  const [cieInput, setCieInput] = useState(''); 
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [paraclinicalsList, setParaclinicalsList] = useState([]);
  const [selectedLabBatchIndex, setSelectedLabBatchIndex] = useState(-1); // -1 significa "Sin laboratorios"

  const [newTreatmentPlanText, setNewTreatmentPlanText] = useState('');
  
  // IDs de edición
  const [editingIllnessEntryId, setEditingIllnessEntryId] = useState(null);
  const [editingPhysicalEntryId, setEditingPhysicalEntryId] = useState(null);
  const [editingTreatmentEntryId, setEditingTreatmentEntryId] = useState(null);
  const [editingEvolutionId, setEditingEvolutionId] = useState(null);

  // --- FUNCION DE CARGA ---
const loadParaclinicals = async (isManualRefresh = false) => {
  if (!patientId) return;

  try {
    const token = localStorage.getItem('token');
    const res = await authFetch(`${API_URL}/api/paraclinicos/historial/${patientId}`);

    if (!res.ok) throw new Error(`Error HTTP ${res.status}`);

    const data = await res.json();
    const safeData = Array.isArray(data) ? data : [];
    setParaclinicalsList(safeData);

    // ✅ LÓGICA DE ALERTA: Solo si es manual o si el último registro es de HOY
    if (safeData.length > 0) {
      const hoy = dayjs().format('DD/MM/YYYY');
      const fechaUltimoLab = safeData[0].fecha_ingreso || safeData[0].fecha;
      
      if (isManualRefresh || fechaUltimoLab === hoy) {
        setSaveMessage(`✅ Sincronizado: Registro de laboratorio del día de hoy detectado.`);
        setTimeout(() => setSaveMessage(''), 3000);
      }
    }
  } catch (e) {
    console.error('❌ Error cargando paraclínicos:', e.message);
  }
};
  // --- CÁLCULO AUTOMÁTICO DE IMC ---
  useEffect(() => {
      const w = parseFloat(newEvolution.weight);
      const h = parseFloat(newEvolution.height); 
      if (w > 0 && h > 0) {
          const heightM = h > 3 ? h / 100 : h;
          const bmiVal = (w / (heightM * heightM)).toFixed(2);
          setNewEvolution(prev => ({ ...prev, bmi: bmiVal }));
      }
  }, [newEvolution.weight, newEvolution.height]);

  // --- PRECIOS, SERVICIOS Y ESPECIALIDADES---
useEffect(() => {
    const fetchClinicalData = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            // Cargamos Precios y Configuración simultáneamente
            const [pricesRes, configRes] = await Promise.all([
                fetch(`${API_URL}/api/finance/prices`, { headers }),
                fetch(`${API_URL}/api/config`, { headers })
            ]);

            if (pricesRes.ok) {
                setAvailablePrices(await pricesRes.json());
            }

            if (configRes.ok) {
                const configData = await configRes.json();
                // ✅ Extraemos las especialidades favoritas de la configuración
                if (configData.favoriteSpecialties) {
                    const parsedSpecs = typeof configData.favoriteSpecialties === 'string' 
                        ? JSON.parse(configData.favoriteSpecialties) 
                        : configData.favoriteSpecialties;
                    setSpecialties(parsedSpecs);
                }
            }
        } catch (e) {
            console.error("Error cargando datos clínicos:", e);
        }
    };
    fetchClinicalData();
}, []); // Se ejecuta una sola vez al cargar la página

  // --- CARGA DE DATOS ---
useEffect(() => {
  const authFetchData = async () => {
    setIsLoading(true);
    try {
      // ================= PACIENTE =================
      const resPatient = await authFetch(
        `${API_URL}/api/patients/${patientId}`
      );

      if (resPatient.status === 401) {
        throw new Error('Sesión expirada. Por favor, vuelva a ingresar.');
      }
      if (!resPatient.ok) {
        throw new Error('Paciente no encontrado');
      }

      const data = await resPatient.json();
      setPatient(data);

      if (data.pendingStudies) {
        setActivePendings(data.pendingStudies);
      }

      if (data.medicalHistory) {
        setMedicalHistory(prev => ({
          ...prev,
          ...data.medicalHistory,
          background: {
            ...prev.background,
            ...(data.medicalHistory.background || {}),
            currentMedications:
              data.medicalHistory.background?.currentMedications || []
          },
          gynecoObstetric: {
            ...prev.gynecoObstetric,
            ...(data.medicalHistory.gynecoObstetric || {})
          },
          currentIllness: {
            ...prev.currentIllness,
            ...(data.medicalHistory.currentIllness || {}),
            diagnoses:
              data.medicalHistory.currentIllness?.diagnoses || []
          },
          physicalExam: {
            ...prev.physicalExam,
            ...(data.medicalHistory.physicalExam || {})
          },
          treatment: {
            ...prev.treatment,
            treatmentHistory:
              data.medicalHistory.treatment?.treatmentHistory || []
          },
          currentIllnessHistory:
            data.medicalHistory.currentIllnessHistory || [],
          physicalExamHistory:
            data.medicalHistory.physicalExamHistory || []
        }));
      }

// ================= EVOLUCIONES =================
      const resEvos = await authFetch(
        `${API_URL}/api/evolutions/${patientId}`
      );

      if (resEvos.ok) {
        const evosData = await resEvos.json();
        const safeEvos = Array.isArray(evosData) ? evosData : [];
        setEvolutionsList(safeEvos);

        // ✅ Lógica insertada: decidir el tipo de consulta automáticamente
        if (safeEvos.length === 0) {
            // Si el array está vacío, es un paciente nuevo (Primera vez)
            setNewEvolution(prev => ({ ...prev, visitType: 'NUEVO' }));
        } else {
            // Si ya tiene registros, por defecto es un control (Sucesivo)
            setNewEvolution(prev => ({ ...prev, visitType: 'SUCESIVO' }));
        }
      }

      // ================= RECETAS =================
      const resRecipes = await authFetch(
        `${API_URL}/api/recipes?patientId=${patientId}`
      );

      if (resRecipes.ok) {
        const recipesData = await resRecipes.json();
        const safeRecipes = Array.isArray(recipesData)
          ? recipesData
          : [];

        setUnifiedHistory(
          safeRecipes
            .filter(r => r.type !== 'study_request')
            .sort(
              (a, b) =>
                new Date(b.createdAt) - new Date(a.createdAt)
            )
        );
      }

      // ================= PARACLÍNICOS =================
      console.log('🔬 Cargando paraclínicos automáticamente...');
      await loadParaclinicals();

      // ================= MACROS =================
      authFetchMacros();

    } catch (error) {
      console.error('❌ Error en carga clínica:', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (patientId) {
    authFetchData();
  }
}, [patientId]);


  // --- BÚSQUEDA CIE-10 ---
  useEffect(() => {
      const searchCIE10 = async () => {
          if (!cieInput || cieInput.length < 2) { setCieOptions([]); return; }
          setCieLoading(true);
          try {
              const res = await authFetch(`${API_URL}/api/cie10?q=${cieInput}`);
              if (res.ok) { const data = await res.json(); setCieOptions(Array.isArray(data) ? data : []); }
          } catch (e) { console.error(e); } finally { setCieLoading(false); }
      };
      const timeoutId = setTimeout(() => { searchCIE10(); }, 500);
      return () => clearTimeout(timeoutId);
  }, [cieInput]);

  // --- FUNCIONES MACROS (PLANTILLAS) ---
  const authFetchMacros = async () => {
    try {
        const res = await authFetch(`${API_URL}/api/medical-templates`);
        if (res.ok) {
            const data = await res.json();
            setMacros(Array.isArray(data) ? data : []);
        }
		} catch (e) { console.error("Error macros", e); }
	};

  const handleOpenMacro = (targetField) => {
    setMacroDialog({ open: true, targetField });
    setIsCreatingMacro(false); 
  };

  const handleCreateMacro = async () => {
      if (!newMacroForm.title || !newMacroForm.content) return alert("Complete título y contenido");
      
      try {
          let category = 'general';
          if (macroDialog.targetField === 'currentIllness') category = 'illness';
          if (macroDialog.targetField === 'physicalExam') category = 'physical';
          if (macroDialog.targetField === 'evolution') category = 'evolution';

          const res = await authFetch(`${API_URL}/api/medical-templates`, {
			 method: 'POST',
			 body: JSON.stringify({ ...newMacroForm, category })
		  });

          if (res.ok) {
              authFetchMacros();
              setNewMacroForm({ title: '', content: '' });
              setIsCreatingMacro(false); 
          }
      } catch (e) { alert("Error al guardar plantilla"); }
  };

  const handleDeleteMacro = async (id, e) => {
      e.stopPropagation();
      if (!window.confirm("¿Borrar esta plantilla?")) return;
      await authFetch(`${API_URL}/api/medical-templates/${id}`, { method: 'DELETE' });
      authFetchMacros();
  };

  const handleSelectMacro = (text) => {
    const field = macroDialog.targetField;
    const currentText = field === 'currentIllness' ? (medicalHistory.currentIllness.description || "") 
                      : field === 'physicalExam' ? (medicalHistory.physicalExam.description || "") 
                      : field === 'evolution' ? (newEvolution.evolutionText || "") 
                      : '';
    
    const newText = currentText ? `${currentText}\n${text}` : text;

    if (field === 'currentIllness') {
      setMedicalHistory(p => ({...p, currentIllness: { ...p.currentIllness, description: newText }}));
    } else if (field === 'physicalExam') {
      setMedicalHistory(p => ({...p, physicalExam: { ...p.physicalExam, description: newText }}));
    } else if (field === 'evolution') {
      setNewEvolution(p => ({...p, evolutionText: newText}));
    }
    setMacroDialog({ open: false, targetField: null });
  };

  const filteredMacros = (macros || []).filter(m => {
      const field = macroDialog.targetField;
      if (field === 'currentIllness') return m.category === 'illness';
      if (field === 'physicalExam') return m.category === 'physical';
      if (field === 'evolution') return m.category === 'evolution';
      return true;
  });

  const sendWhatsappMessage = (type) => {
    if (!patient?.phone) return alert("El paciente no tiene teléfono registrado.");
    let message = "";
    const phone = patient.phone.replace(/[^0-9]/g, '');
    const greeting = `Hola ${patient.name}, le escribe el Dr. ${user?.name || 'su médico'}.`;

    if (type === 'general') {
      message = `${greeting} Le escribo para confirmar su asistencia o enviarle información.`;
    } else if (type === 'evolution') {
      const date = newEvolution.date ? dayjs(newEvolution.date).format('DD/MM/YYYY') : dayjs().format('DD/MM/YYYY');
      message = `${greeting} Resumen de su consulta del ${date}:\n\n*Peso:* ${newEvolution.weight || 'N/A'} kg\n*Tensión:* ${newEvolution.systolic}/${newEvolution.diastolic}\n\n*Indicaciones:* ${newEvolution.evolutionText}`;
    }
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

const processPhysicalAndSave = async () => {
  const { description } = medicalHistory.physicalExam;
  
  if (!description || !description.trim()) {
      alert('La reseña del examen físico no puede estar vacía.');
      return;
  }

  // ✅ Seleccionar el lote según lo que marcó el médico en el diálogo
  const labToAttach = selectedLabBatchIndex === -1 ? null : paraclinicalsList[selectedLabBatchIndex];

  const newEntry = { 
      id: nanoid(), 
      date: dayjs().format('YYYY-MM-DD HH:mm'), 
      createdBy: user?.id, 
      doctorName: user?.name || 'Médico', 
      description: description,
      labsAttached: labToAttach // Puede ser null si eligió "No vincular"
  };

  const updatedHistoryState = {
      ...medicalHistory,
      physicalExamHistory: [newEntry, ...(medicalHistory.physicalExamHistory || [])],
      physicalExam: { description: '', paraclinicals: '' } 
  };

  try {
      const res = await authFetch(`${API_URL}/api/patients/${patientId}`, { 
          method: 'PUT', 
          body: JSON.stringify({ ...patient, medicalHistory: updatedHistoryState }) 
      });

      if(res.ok) {
          setMedicalHistory(updatedHistoryState); 
          setSaveMessage(labToAttach ? '✅ Examen y Laboratorios vinculados' : '✅ Examen guardado (Sin labs)');
          setIsEditing(false);
          const refreshed = await res.json();
          setPatient(refreshed);
          setOpenConfirmDialog(false);
      }
  } catch(e) {
      alert("Error de conexión al guardar.");
  }
  
  setTimeout(() => setSaveMessage(''), 3000);
};

  const handleSaveHistory = async () => {
      try {
          const updatedPatient = { ...patient, pendingStudies: activePendings, medicalHistory };
          
          const res = await authFetch(`${API_URL}/api/patients/${patient.id}`, { 
			method: 'PUT', 
			body: JSON.stringify(updatedPatient) 
		  });

          if (res.ok) {
              setSaveMessage('✅ Cambios generales guardados correctamente.');
              setIsEditing(false); // Opcional: Si quieres salir del modo edición al guardar
          } else {
              throw new Error('Error en la respuesta del servidor');
          }

      } catch(e) { 
          console.error(e);
          setSaveMessage('❌ Error al guardar los cambios.'); 
      } finally {
          // El timeout debe ir aquí o manejarse después de setear el mensaje
          setTimeout(() => setSaveMessage(''), 4000);
      }
  };

  const handleDeleteHistory = async () => { 
      if(!window.confirm("ADVERTENCIA: ¿Limpiar campos de texto? Esto NO borra evoluciones ni recetas.")) return;
      try {
          const emptyHistory = { ...patient, medicalHistory: {}, pendingStudies: [] };
          await authFetch(`${API_URL}/api/patients/${patient.id}`, { 
			method: 'PUT', 
			body: JSON.stringify(emptyHistory) 
		  });	
          window.location.reload();
      } catch(e) { alert("Error al limpiar"); }
  };

  const handleAddAlert = () => { if(!newPendingText.trim()) return; setActivePendings(prev => [{id: nanoid(), text: newPendingText, date: dayjs().format('DD/MM/YYYY'), resolved: false}, ...(prev || [])]); setNewPendingText(''); };
  const handleResolveAlert = (id) => setActivePendings(prev => (prev || []).filter(a => a.id !== id));
  const handleHabitChange = (n, f, v) => setMedicalHistory(p => ({...p, background: {...p.background, habits: {...p.background.habits, [n]: {...p.background.habits[n], [f]: v}}}}));
  const handleOtherBackgroundChange = (n, v) => setMedicalHistory(p => ({...p, background: {...p.background, otherBackground: {...p.background.otherBackground, [n]: v}}}));
  const handleAddBackgroundMed = () => { if(!newBackgroundMed.trim()) return; setMedicalHistory(p => ({...p, background: {...p.background, currentMedications: [{id: nanoid(), text: newBackgroundMed, date: dayjs().format('DD/MM/YYYY')}, ...(p.background.currentMedications || [])]}})); setNewBackgroundMed(''); };
  const handleDeleteBackgroundMed = (id) => setMedicalHistory(p => ({...p, background: {...p.background, currentMedications: (p.background.currentMedications || []).filter(m => m.id !== id)}}));
  const handleRemoveDiagnosis = (id) => setMedicalHistory(p => ({...p, currentIllness: {...p.currentIllness, diagnoses: (p.currentIllness.diagnoses || []).filter(d => d.id !== id)}}));
  const handleAddDiagnosis = () => {
  // Validación: al menos uno debe estar lleno
  if (!selectedDiag && !manualDiag.trim()) {
    return alert('Debe ingresar al menos un diagnóstico (CIE-10 o manual).');
  }
  
  // Construcción del diagnóstico combinado
  let codigo = 'MANUAL';
  let descripcion = '';
  
  if (selectedDiag && manualDiag.trim()) {
    // AMBOS CAMPOS LLENOS: Combinar
    codigo = selectedDiag.code;
    descripcion = `${selectedDiag.description} - ${manualDiag.trim()}`;
  } else if (selectedDiag) {
    // SOLO CIE-10
    codigo = selectedDiag.code;
    descripcion = selectedDiag.description;
  } else {
    // SOLO MANUAL
    codigo = 'MANUAL';
    descripcion = manualDiag.trim();
  }
  
  const newDiag = { 
    id: nanoid(), 
    cie10Code: codigo, 
    description: descripcion 
  };
  
  setMedicalHistory(p => ({
    ...p, 
    currentIllness: {
      ...p.currentIllness, 
      diagnoses: [...(p.currentIllness.diagnoses || []), newDiag]
    }
  }));
  
  // Limpiar y cerrar
  setOpenDiagDialog(false); 
  setSelectedDiag(null); 
  setManualDiag('');
};

  const handleSaveCurrentIllnessEntry = () => {
    const { description, diagnoses } = medicalHistory.currentIllness;
    if (!description || !description.trim()) return alert('Descripción vacía.');
    const newEntry = { id: editingIllnessEntryId || nanoid(), date: dayjs().format('YYYY-MM-DD HH:mm'), createdBy: user?.id, doctorName: user?.name, description, diagnoses };
    if (editingIllnessEntryId) setMedicalHistory(p => ({...p, currentIllnessHistory: (p.currentIllnessHistory || []).map(e => e.id === editingIllnessEntryId ? newEntry : e)}));
    else setMedicalHistory(p => ({...p, currentIllnessHistory: [newEntry, ...(p.currentIllnessHistory || [])]}));
    setMedicalHistory(p => ({...p, currentIllness: {description: '', diagnoses: []}})); setEditingIllnessEntryId(null);
  };
  const handleEditCurrentIllnessEntry = (e) => { setMedicalHistory(p => ({...p, currentIllness: {description: e.description, diagnoses: e.diagnoses}})); setEditingIllnessEntryId(e.id); };
  const handleDeleteCurrentIllnessEntry = (id) => { if(window.confirm('¿Eliminar?')) setMedicalHistory(p => ({...p, currentIllnessHistory: (p.currentIllnessHistory || []).filter(e => e.id !== id)})); };

  const handleSavePhysicalExamEntry = () => {
      const { description } = medicalHistory.physicalExam;
      if (!description || !description.trim()) return alert('La reseña del examen físico no puede estar vacía.');
      const labSnapshot = (paraclinicalsList || []).length > 0 ? paraclinicalsList[0] : null;
      const newEntry = { 
          id: editingPhysicalEntryId || nanoid(), 
          date: dayjs().format('YYYY-MM-DD HH:mm'), 
          createdBy: user?.id, 
          doctorName: user?.name, 
          description,
          labsAttached: labSnapshot 
      };
      if (editingPhysicalEntryId) { 
          setMedicalHistory(p => ({
              ...p, 
              physicalExamHistory: (p.physicalExamHistory || []).map(e => e.id === editingPhysicalEntryId ? newEntry : e)
          })); 
          setEditingPhysicalEntryId(null); 
      } else { 
          setMedicalHistory(p => ({...p, physicalExamHistory: [newEntry, ...(p.physicalExamHistory || [])]})); 
      }
      setMedicalHistory(p => ({...p, physicalExam: { ...p.physicalExam, description: '' }}));
      setSaveMessage('Registro preparado.');
      setTimeout(() => setSaveMessage(''), 3000);
  };
  const handleEditPhysicalExamEntry = (e) => { setMedicalHistory(p => ({...p, physicalExam: { description: e.description, paraclinicals: e.paraclinicals }})); setEditingPhysicalEntryId(e.id); };
  const handleDeletePhysicalExamEntry = async (id) => {
    if (!window.confirm('¿Está seguro de que desea eliminar este registro del historial físico definitivamente?')) return;
    // 1. Filtrar la lista localmente
    const updatedHistory = (medicalHistory.physicalExamHistory || []).filter(e => e.id !== id);
    // 2. Preparar el objeto de actualización para la DB
    const updatedHistoryState = {
        ...medicalHistory,
        physicalExamHistory: updatedHistory
    };
    try {
        // 3. Persistir en el servidor (Esto es lo que faltaba para que no vuelvan a aparecer)
        const res = await authFetch(`${API_URL}/api/patients/${patientId}`, { 
            method: 'PUT', 
            body: JSON.stringify({ ...patient, medicalHistory: updatedHistoryState }) 
        });
        if(res.ok) {
            setMedicalHistory(updatedHistoryState);
            setSaveMessage('✅ Registro eliminado correctamente');
        } else {
            alert("Error al eliminar en el servidor");
        }
    } catch(e) {
        console.error(e);
        alert("Error de conexión");
    }
    setTimeout(() => setSaveMessage(''), 3000);
};

  const handleSaveTreatmentEntry = () => {
    if (!newTreatmentPlanText || !newTreatmentPlanText.trim()) return;
    const newEntry = { id: editingTreatmentEntryId || nanoid(), date: dayjs().format('YYYY-MM-DD HH:mm'), createdBy: user?.id, doctorName: user?.name, text: newTreatmentPlanText };
    if (editingTreatmentEntryId) { setMedicalHistory(p => ({...p, treatment: {...p.treatment, treatmentHistory: (p.treatment.treatmentHistory || []).map(e => e.id === editingTreatmentEntryId ? newEntry : e)}})); setEditingTreatmentEntryId(null); }
    else setMedicalHistory(p => ({...p, treatment: {...p.treatment, treatmentHistory: [newEntry, ...(p.treatment.treatmentHistory || [])]}}));
    setNewTreatmentPlanText('');
  };
  const handleEditTreatmentEntry = (e) => { setNewTreatmentPlanText(e.text); setEditingTreatmentEntryId(e.id); };
  const handleDeleteTreatmentEntry = (id) => { if(window.confirm('¿Eliminar?')) setMedicalHistory(p => ({...p, treatment: {...p.treatment, treatmentHistory: (p.treatment.treatmentHistory || []).filter(e => e.id !== id)}})); };

const handleSaveEvolution = async () => {
    if (!newEvolution.evolutionText || !newEvolution.evolutionText.trim()) {
        return alert("El campo 'Nota de Evolución' es obligatorio.");
    }

    const dataToSubmit = {
        date: new Date(newEvolution.date), 
        evolutionText: newEvolution.evolutionText,
		consultationReason: newEvolution.consultationReason, 
        // ✅ Aseguramos que si visitType es null, envíe 'SUCESIVO' por defecto
        visitType: newEvolution.visitType || 'SUCESIVO', 
        weight: newEvolution.weight ? parseFloat(newEvolution.weight) : null,
        height: newEvolution.height ? parseFloat(newEvolution.height) : null,
        bmi: newEvolution.bmi ? parseFloat(newEvolution.bmi) : null,
        systolic: newEvolution.systolic ? parseInt(newEvolution.systolic) : null,
        diastolic: newEvolution.diastolic ? parseInt(newEvolution.diastolic) : null,
        heartRate: newEvolution.heartRate ? parseInt(newEvolution.heartRate) : null,
        temperature: newEvolution.temperature ? parseFloat(newEvolution.temperature) : null,
		specialty: newEvolution.specialty,
		amountPaid: parseFloat(newEvolution.amountPaid.toString()), // 👈 ENVIAR EL MONTO
        servicePriceId: newEvolution.servicePriceId,
		patientId: patientId,
        doctorId: user?.role === 'medico' || user?.role === 'administrador' ? user.id : null
    };

    try {
        const method = editingEvolutionId ? 'PUT' : 'POST';
        const url = editingEvolutionId 
            ? `${API_URL}/api/evolutions/${editingEvolutionId}` 
            : `${API_URL}/api/evolutions`;

        const res = await authFetch(url, { 
			method: method, 
			body: JSON.stringify(dataToSubmit) 
		});

        if (res.ok) {
            const savedEvo = await res.json();
            if (editingEvolutionId) {
                setEvolutionsList(prev => (prev || []).map(e => e.id === editingEvolutionId ? savedEvo : e));
                setEditingEvolutionId(null);
            } else { 
                setEvolutionsList(prev => [savedEvo, ...(prev || [])]); 
            }
            
            setNewEvolution({ 
                date: dayjs(), 
                evolutionText: '', 
                weight: '', 
                height: '', 
                bmi: '', 
                systolic: '', 
                diastolic: '', 
                heartRate: '', 
                temperature: '' 
            });
            setSaveMessage("Evolución guardada con éxito.");
        } else {
            const errorData = await res.json();
            console.error("Error detallado del servidor:", errorData);
            alert(`Error: ${errorData.error || 'No se pudo guardar la evolución'}`);
        }
    } catch (e) { 
        console.error("Error de conexión:", e);
        alert("Error de comunicación con el servidor.");
    }
    setTimeout(() => setSaveMessage(''), 3000);
  };
  
  const handleEditEvolution = (e: any) => { 
    setNewEvolution({  
          date: dayjs(e.date), 
          evolutionText: e.evolutionText || '', 
          treatmentAndConduct: '', 
          weight: e.weight || '', 
          height: e.height || '', 
          bmi: e.bmi || '', 
          systolic: e.systolic || '', 
          diastolic: e.diastolic || '', 
          heartRate: e.heartRate || '', 
          temperature: e.temperature || '',
          // ✅ Cargamos el tipo de visita guardado en la DB
          visitType: e.visitType || 'SUCESIVO',
          amountPaid: e.amountPaid || 0, 
		  servicePriceId: e.servicePriceId || '' 
      }); 
      setEditingEvolutionId(e.id); 
  };
  const handleDeleteEvolution = async (id) => { if (!window.confirm("¿Eliminar evolución?")) return; await authFetch(`${API_URL}/api/evolutions/${id}`, { method: 'DELETE' }); setEvolutionsList(prev => (prev || []).filter(e => e.id !== id)); };

  const chartData = (evolutionsList || []).slice().reverse().map(e => ({ date: new Date(e.date).toLocaleDateString(), Peso: e.weight, Sistolica: e.systolic, Diastolica: e.diastolic })).filter(e => e.Peso || e.Sistolica);

  if (isLoading) return <Box sx={{display:'flex', justifyContent:'center', p:5}}><CircularProgress /></Box>;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>{patient?.name} {patient?.lastName}</Typography>
              <Typography variant="subtitle1" color="text.secondary">C.I.: {patient?.cedula || patient?.idNumber} | Edad: {patient?.birthDate ? dayjs().diff(dayjs(patient.birthDate), 'year') : 'N/A'} años</Typography>
              {patient?.phone && (
                <Box sx={{ mt: 1 }}>
                    <Button size="small" startIcon={<WhatsAppIcon />} variant="contained" color="success" onClick={() => sendWhatsappMessage('general')} sx={{ mr: 1 }}>Hola</Button>
                    {tabValue === '5' && (<Button size="small" variant="outlined" color="success" onClick={() => sendWhatsappMessage('evolution')}>Enviar Resumen</Button>)}
                </Box>
              )}
          </Box>
          <IconButton onClick={() => navigate('/patients')}><ArrowBackIcon /></IconButton>
        </Box>

        {saveMessage && <Alert severity="success" sx={{ mb: 2 }}>{saveMessage}</Alert>}
        
        <Box sx={{ mb: 2, display: 'flex', gap: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          {isEditing ? (
              <><Button onClick={handleSaveHistory} variant="contained" color="success" startIcon={<SaveIcon />}>Guardar Cambios Generales</Button>
              <Button onClick={() => setIsEditing(false)}>Cancelar Edición</Button></>
          ) : (<Button onClick={() => setIsEditing(true)} variant="contained" startIcon={<EditIcon />}>Editar Historia</Button>)}
          {isAdmin && (<Button color="error" startIcon={<DeleteForeverIcon />} onClick={handleDeleteHistory}>Limpiar Campos</Button>)}
        </Box>

        <Paper sx={{ width: '100%' }}>
          <TabContext value={tabValue}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList onChange={(e, val) => {
                setTabValue(val);
                if (val === '3') {
                  loadParaclinicals();
                }
              }} variant="scrollable">
                <Tab label="1. Antecedentes" value="1" />
                <Tab label="2. Enfermedad Actual" value="2" />
                <Tab label="3. Examen Físico y Paraclínicos" value="3" />
                <Tab label="4. Tratamiento" value="4" />
                <Tab label="5. Evoluciones y Signos" value="5" />
              </TabList>
            </Box>


            {/* --- PESTAÑA 1: ANTECEDENTES --- */}
            <TabPanel value="1">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}><TextField label="Alergias" fullWidth multiline rows={2} value={medicalHistory.allergies || ""} onChange={(e) => setMedicalHistory(prev => ({ ...prev, allergies: e.target.value }))} disabled={!isEditing} InputLabelProps={{ style: { color: 'red' } }} /></Grid>
                        <Grid item xs={12} md={6}><TextField label="Patologías Previas" fullWidth multiline rows={2} value={medicalHistory.background.previousPathologies || ""} onChange={(e) => setMedicalHistory(prev => ({ ...prev, background: { ...prev.background, previousPathologies: e.target.value } }))} disabled={!isEditing} /></Grid>
                        <Grid item xs={12} md={6}><TextField label="Quirúrgicos" fullWidth multiline rows={2} value={medicalHistory.background.surgical || ""} onChange={(e) => setMedicalHistory(prev => ({ ...prev, background: { ...prev.background, surgical: e.target.value } }))} disabled={!isEditing} /></Grid>
                        <Grid item xs={12}><TextField label="Padres" fullWidth multiline rows={2} value={medicalHistory.background.family.parents || ""} onChange={(e) => setMedicalHistory(prev => ({ ...prev, background: { ...prev.background, family: { ...prev.background.family, parents: e.target.value } } }))} disabled={!isEditing} /></Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={6}><Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9', height: '100%' }}><Typography variant="subtitle2" color="primary">Hábitos Psicobiológicos</Typography><Divider sx={{ mb: 1 }} />{['tabaquico', 'alcoholico', 'cafeinico', 'medicamentoso'].map((habit, index) => (<Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}><FormControlLabel control={<Checkbox checked={medicalHistory.background.habits[habit]?.active || false} onChange={(e) => handleHabitChange(habit, 'active', e.target.checked)} disabled={!isEditing} />} label={<Typography variant="body2">{habit.charAt(0).toUpperCase() + habit.slice(1)}:</Typography>} sx={{ minWidth: 120 }} /><TextField size="small" fullWidth placeholder="Aclaraciones" value={medicalHistory.background.habits[habit]?.notes || ''} onChange={(e) => handleHabitChange(habit, 'notes', e.target.value)} disabled={!isEditing} /></Box>))}</Paper></Grid>
                <Grid item xs={12} md={6}><Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9' }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><MedicationIcon color="primary" /><Typography variant="subtitle2" color="primary">Tratamiento Farmacológico Actual (Reportado)</Typography></Box><Box sx={{ display: 'flex', gap: 1, mb: 2 }}><TextField size="small" fullWidth placeholder="Medicamento actual..." disabled={!isEditing} value={newBackgroundMed} onChange={(e) => setNewBackgroundMed(e.target.value)} /><Button variant="contained" size="small" onClick={handleAddBackgroundMed} disabled={!isEditing}>Añadir</Button></Box><List dense sx={{ maxHeight: 150, overflowY: 'auto', bgcolor: 'white', borderRadius: 1, border: '1px solid #eee' }}>{(!medicalHistory.background.currentMedications || medicalHistory.background.currentMedications.length === 0) && (<ListItem><ListItemText secondary="No hay medicamentos reportados." /></ListItem>)}{(medicalHistory.background.currentMedications || []).map((med) => (<ListItem key={med.id} divider><ListItemText primary={med.text} secondary={`Registrado el: ${med.date}`} />{isEditing && (<ListItemSecondaryAction><IconButton size="small" onClick={() => handleDeleteBackgroundMed(med.id)} edge="end"><DeleteIcon fontSize="small" /></IconButton></ListItemSecondaryAction>)}</ListItem>))}</List></Paper></Grid>
<Grid item xs={12} md={6}>
  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9', height: '100%' }}>
    <Typography variant="subtitle2" color="primary" gutterBottom>Otros Antecedentes</Typography>
    <Divider sx={{ mb: 1 }} />
    
    {/* CONTENEDOR EN UNA SOLA LÍNEA SIN SALTO */}
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
      
      {/* HIJOS (Ancho fijo pequeño) */}
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Hijos:</Typography>
      <TextField 
        size="small" 
        type="number" 
        value={medicalHistory.background.otherBackground.childrenCount} 
        onChange={(e) => handleOtherBackgroundChange('childrenCount', parseInt(e.target.value) || 0)} 
        disabled={!isEditing} 
        sx={{ width: '55px' }} // Ancho reducido para ganar espacio
      />

      {/* PAREJA SEXUAL (Margen reducido) */}
      <FormControlLabel
        sx={{ ml: 0.5, mr: 0 }} // Ajuste de márgenes internos
        control={
          <Checkbox 
            size="small"
            checked={medicalHistory.background.otherBackground.sexualPartner || false} 
            onChange={(e) => handleOtherBackgroundChange('sexualPartner', e.target.checked)}
            disabled={!isEditing}
          />
        }
        label={<Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Pareja sexual</Typography>}
      />

      {/* ACLARATORIA (Ocupa todo el resto del espacio) */}
      <TextField 
        size="small" 
        label="Aclaratoria" 
        fullWidth
        sx={{ flex: 1 }} // flex: 1 obliga a estirarse sin saltar de línea
        value={medicalHistory.background.otherBackground.sexualPartnerNotes || ''} 
        onChange={(e) => handleOtherBackgroundChange('sexualPartnerNotes', e.target.value)} 
        disabled={!isEditing} 
      />
    </Box>

    <TextField 
      label="Cáncer o Enfermedades Hereditarias en la Familia" 
      fullWidth 
      multiline 
      rows={4} 
      value={medicalHistory.background.otherBackground.hereditaryConditions || ""} 
      onChange={(e) => handleOtherBackgroundChange('hereditaryConditions', e.target.value)} 
      disabled={!isEditing} 
      helperText="Ej: Cáncer de mama (madre), Hemofilia (tío)" 
    />
  </Paper>
</Grid>
                <Grid item xs={12}><Divider sx={{my:2}} /><Typography variant="subtitle2" color="text.secondary">Gineco-Obstétricos</Typography></Grid>
                <Grid item xs={6} md={3}><TextField label="Menarquia" fullWidth value={medicalHistory.gynecoObstetric.menarche.age || ""} onChange={(e) => setMedicalHistory(prev => ({ ...prev, gynecoObstetric: { ...prev.gynecoObstetric, menarche: { ...prev.gynecoObstetric.menarche, age: e.target.value } } }))} disabled={!isEditing} /></Grid>
                <Grid item xs={6} md={3}><TextField label="Menopausia" fullWidth value={medicalHistory.gynecoObstetric.menopause || ""} onChange={(e) => setMedicalHistory(prev => ({ ...prev, gynecoObstetric: { ...prev.gynecoObstetric, menopause: e.target.value } }))} disabled={!isEditing} /></Grid>
                <Grid item xs={6} md={3}><TextField label="Gestas" fullWidth value={medicalHistory.gynecoObstetric.gestas || ""} onChange={(e) => setMedicalHistory(prev => ({ ...prev, gynecoObstetric: { ...prev.gynecoObstetric, gestas: e.target.value } }))} disabled={!isEditing} /></Grid>
                <Grid item xs={12} md={3}><DatePicker label="Última Menstruación" value={medicalHistory.gynecoObstetric.lastMenstruation ? dayjs(medicalHistory.gynecoObstetric.lastMenstruation) : null} onChange={(val) => setMedicalHistory(prev => ({ ...prev, gynecoObstetric: { ...prev.gynecoObstetric, lastMenstruation: val ? val.format('YYYY-MM-DD') : null } } ))} slotProps={{ textField: { fullWidth: true, disabled: !isEditing } }} /></Grid>
              </Grid>
            </TabPanel>


            {/* --- PESTAÑA 2: ENFERMEDAD ACTUAL --- */}
            <TabPanel value="2">
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>{editingIllnessEntryId ? 'Editando Registro' : 'Nueva Enfermedad Actual'}</Typography>
                        <TextField 
                            label="Describa la enfermedad actual" 
                            fullWidth multiline rows={8} 
                            value={medicalHistory.currentIllness.description || ""} 
                            onChange={(e) => setMedicalHistory(p => ({...p, currentIllness: {...p.currentIllness, description: e.target.value}}))} 
                            disabled={!isEditing} 
                            InputProps={{
                              endAdornment: isEditing && (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => handleOpenMacro('currentIllness')} title="Usar Macro (Plantilla)">
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                      <FlashOnIcon color="warning" />
                                    </Box>
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}><Typography variant="h6" gutterBottom>Diagnósticos CIE-10</Typography><Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, minHeight: 120, border: '1px solid #eee', p: 1, borderRadius: 1, overflowY: 'auto' }}>{(medicalHistory.currentIllness.diagnoses || []).map(diag => (<Chip key={diag.id} label={`${diag.cie10Code} - ${diag.description}`} onDelete={isEditing ? () => handleRemoveDiagnosis(diag.id) : undefined} sx={{ mr: 1, mb: 0.5 }} />))}</Box><Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpenDiagDialog(true)} disabled={!isEditing}>Añadir Diagnóstico</Button></Grid>
                </Grid>
                <Box sx={{ mt: 3, p: 2, borderTop: '1px solid #ddd' }}><Button variant="contained" onClick={handleSaveCurrentIllnessEntry} disabled={!isEditing} startIcon={<SaveIcon />}>{editingIllnessEntryId ? 'Actualizar Registro' : 'Guardar ENFERMEDAD ACTUAL'}</Button>{editingIllnessEntryId && (<Button onClick={() => { setEditingIllnessEntryId(null); setMedicalHistory(p => ({...p, currentIllness: {description: '', diagnoses: []}})); }} sx={{ml: 1}}>Cancelar</Button>)}</Box>
                <Box sx={{ mt: 2, maxHeight: 400, overflowY: 'auto' }}><Typography variant="h6" gutterBottom>Historial</Typography>{(!medicalHistory.currentIllnessHistory || medicalHistory.currentIllnessHistory.length === 0) ? (<Typography variant="body2" color="text.secondary">Sin registros.</Typography>) : (medicalHistory.currentIllnessHistory.map(entry => { const canEdit = isAdmin || user?.id === entry.createdBy; return (<Card key={entry.id} sx={{ mb: 2 }}><CardHeader title={`Dr. ${entry.doctorName}`} subheader={entry.date} action={<Box>{canEdit ? (<><IconButton onClick={() => handleEditCurrentIllnessEntry(entry)}><EditIcon /></IconButton><IconButton onClick={() => handleDeleteCurrentIllnessEntry(entry.id)} color="error"><DeleteIcon /></IconButton></>) : (<IconButton disabled><VisibilityIcon /></IconButton>)}</Box>} /><CardContent><Typography variant="body2"><strong>Descripción:</strong> {entry.description}</Typography></CardContent></Card>); }))}</Box>
            </TabPanel>


{/* --- PESTAÑA 3: EXAMEN FISICO Y PARACLINICOS --- */}
            <TabPanel value="3">
                <Grid container spacing={3}>
                    <Grid item xs={12} md={5}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fdfdfd', borderTop: '4px solid #1976d2', mb: 2 }}>
                            <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>Nueva Reseña Física</Typography>
                            <TextField 
                                label="Hallazgos y Exploración Actual" 
                                fullWidth 
                                multiline 
                                rows={8} 
                                value={medicalHistory.physicalExam.description || ""} 
                                onChange={(e) => setMedicalHistory(p => ({...p, physicalExam: {...p.physicalExam, description: e.target.value}}))} 
                                disabled={!isEditing} 
                                placeholder="Escriba aquí..."
                                InputProps={{
                                  endAdornment: isEditing && (
                                    <InputAdornment position="end">
                                      <IconButton onClick={() => handleOpenMacro('physicalExam')} title="Usar Macro (Plantilla)">
                                        <FlashOnIcon color="warning" />
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }}
                            />
                            <Button variant="contained" size="large" fullWidth sx={{ mt: 2 }} startIcon={<SaveIcon />} onClick={() => setOpenConfirmDialog(true)} disabled={!isEditing || !medicalHistory.physicalExam.description?.trim()}>
                                {editingPhysicalEntryId ? 'Actualizar Registro' : 'Vincular y Guardar'}
                            </Button>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 2, borderLeft: '5px solid #2e7d32', bgcolor: '#f1f8e9' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" color="success.main" fontWeight="bold">Laboratorios Disponibles ({(paraclinicalsList || []).length})</Typography>
                                <Tooltip title="Actualizar"><IconButton size="small" onClick={loadParaclinicals} color="success"><FlashOnIcon fontSize="small" /></IconButton></Tooltip>
                            </Box>
                            <Box sx={{ maxHeight: '500px', minHeight: '200px', overflowY: 'auto', pr: 1 }}>
                                {(!paraclinicalsList || paraclinicalsList.length === 0) ? (<Alert severity="info">No hay resultados.</Alert>) : (
                                    paraclinicalsList.map((lote, index) => (
                                        <Card key={index} sx={{ mb: 1.5, cursor: 'pointer' }} onClick={() => { const detailsEl = document.getElementById(`details-lote-${index}`); if (detailsEl) detailsEl.style.display = detailsEl.style.display === 'none' ? 'block' : 'none'; }}>
                                            <CardHeader avatar={<Avatar sx={{ bgcolor: '#4caf50', width: 32, height: 32 }}><BiotechIcon fontSize="small" /></Avatar>} title={`Laboratorio #${index + 1}`} subheader={lote.fecha_ingreso || lote.fecha} />
                                            <Box id={`details-lote-${index}`} sx={{ display: 'none', px: 2, pb: 2 }}>
                                                {Object.entries(lote || {}).map(([key, val]) => (key !== 'fecha' && key !== 'fecha_ingreso' && key !== '_orden' && <Typography key={key} variant="caption" display="block">• {key}: {val}</Typography>))}
                                            </Box>
                                        </Card>
                                    ))
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Typography variant="h6" color="primary" gutterBottom fontWeight="bold"><AccessibilityNewIcon /> Historial</Typography>
                        <Box sx={{ maxHeight: '68vh', overflowY: 'auto' }}>
                            {(!medicalHistory.physicalExamHistory || medicalHistory.physicalExamHistory.length === 0) ? (<Typography>No hay registros.</Typography>) : (
                                [...medicalHistory.physicalExamHistory].sort((a, b) => new Date(b.date) - new Date(a.date)).map((entry) => (
                                    <Card key={entry.id} sx={{ mb: 2, borderLeft: '6px solid #1976d2' }}>
                                        <CardHeader 
                                            title={dayjs(entry.date).format('DD/MM/YYYY')} 
                                            subheader={`Dr. ${entry.doctorName}`} 
                                            action={
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <IconButton size="small" onClick={() => { const el = document.getElementById(`view-phys-${entry.id}`); if(el) el.style.display = el.style.display === 'none' ? 'block' : 'none'; }}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                    {/* ✅ BOTÓN ELIMINAR CON PRIVILEGIOS */}
                                                    {(isAdmin || user?.id === entry.createdBy) && (
                                                        <IconButton 
                                                            size="small" 
                                                            color="error" 
                                                            onClick={() => handleDeletePhysicalExamEntry(entry.id)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            } 
                                        />
                                        <Box id={`view-phys-${entry.id}`} sx={{ display: 'none', p: 2, bgcolor: '#fcfcfc' }}>
                                            <Typography variant="body2"><strong>RESEÑA:</strong> {entry.description}</Typography>
                                            {entry.labsAttached && (
                                                <Box sx={{ mt: 1, p: 1, bgcolor: '#e3f2fd' }}>
                                                    <Typography variant="caption" fontWeight="bold">LABORATORIOS VINCULADOS:</Typography>
                                                    {Object.entries(entry.labsAttached || {}).map(([k, v]) => (k !== 'fecha' && k !== 'fecha_ingreso' && <Typography key={k} variant="caption" display="block">{k}: {v}</Typography>))}
                                                </Box>
                                            )}
                                        </Box>
                                    </Card>
                                ))
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </TabPanel>


            {/* --- PESTAÑA 4: TRATAMIENTO --- */}
<TabPanel value="4">
  <Grid container spacing={3}>
    <Grid item xs={12} md={5}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fdfdfd' }}>
        <Typography variant="h6" gutterBottom color="primary">Plan de Tratamiento Actualizado</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField 
                fullWidth multiline rows={2} 
                label="Nueva indicación técnica" 
                value={newTreatmentPlanText} 
                onChange={(e) => setNewTreatmentPlanText(e.target.value)} 
                disabled={!isEditing} 
            />
            <Button 
                variant="contained" 
                onClick={handleSaveTreatmentEntry} 
                disabled={!isEditing} 
                sx={{ minWidth: 100 }}
            >
                {editingTreatmentEntryId ? 'Actualizar' : 'Añadir'}
            </Button>
        </Box>
        <List dense sx={{ maxHeight: 300, overflowY: 'auto' }}>
            {(medicalHistory.treatment?.treatmentHistory || []).map(entry => (
                <ListItem key={entry.id} divider>
                    <ListItemText 
                        primary={entry.text} 
                        secondary={`Registrado por: Dr. ${entry.doctorName} - ${entry.date}`} 
                    />
                    {isEditing && (isAdmin || user?.id === entry.createdBy) && (
                        <ListItemSecondaryAction>
                            <IconButton size="small" onClick={() => handleEditTreatmentEntry(entry)}><EditIcon fontSize="small"/></IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteTreatmentEntry(entry.id)}><DeleteIcon fontSize="small"/></IconButton>
                        </ListItemSecondaryAction>
                    )}
                </ListItem>
            ))}
        </List>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderLeft: '5px solid #ffa000' }}>
        <Typography variant="h6" gutterBottom color="warning.main">
            Alertas Activas (Estudios Pendientes)
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField 
                fullWidth 
                size="small" 
                label="Nueva alerta o estudio pendiente..." 
                value={newPendingText} 
                onChange={(e) => setNewPendingText(e.target.value)} 
                disabled={!isEditing} 
                placeholder="Ej: Control de Eco Renal"
            />
            <Button 
                variant="contained" 
                color="warning" 
                onClick={handleAddAlert} 
                disabled={!isEditing}
                startIcon={<AddIcon />}
            >
                Añadir
            </Button>
        </Box>

        <List dense>
            {(activePendings || []).map(alert => (
                <ListItem key={alert.id} divider>
                    <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                    <ListItemText 
                        primary={alert.text} 
                        secondary={alert.date} 
                    />
                    {isEditing && (
                        <IconButton onClick={() => handleResolveAlert(alert.id)}>
                            <CheckCircleIcon color="success" />
                        </IconButton>
                    )}
                </ListItem>
            ))}
            {(activePendings || []).length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    No hay estudios o alertas pendientes.
                </Typography>
            )}
        </List>
      </Paper>
    </Grid>

    <Grid item xs={12} md={7}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReceiptLongIcon color="primary" /> Historial de Actos Médicos (Solo Lectura)
      </Typography>
      <Divider sx={{ mb: 2 }} />
      
      <Box sx={{ maxHeight: '65vh', overflowY: 'auto', pr: 1 }}>
        {(unifiedHistory || []).length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No hay registros generados.</Typography>
        ) : (
            (unifiedHistory || []).map((item) => (
                <Card 
                    key={item.id} 
                    sx={{ 
                        mb: 2, 
                        borderLeft: `6px solid ${item.type === 'procedure_execution' ? '#4caf50' : '#1976d2'}`,
                        cursor: 'pointer'
                    }}
                    onClick={() => {
                        const el = document.getElementById(`content-${item.id}`);
                        if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
                    }}
                >
                    <CardHeader
                        avatar={
                            <Avatar sx={{ bgcolor: item.type === 'procedure_execution' ? '#4caf50' : '#1976d2' }}>
                                {item.type === 'procedure_execution' ? <BiotechIcon /> : <MedicationIcon />}
                            </Avatar>
                        }
                        title={item.type === 'procedure_execution' ? item.procedureName : "Receta Médica"}
                        subheader={`Dr. ${item.doctorName} | ${new Date(item.createdAt).toLocaleDateString()}`}
                        action={<IconButton><ExpandMoreIcon /></IconButton>}
                    />
                    <Box id={`content-${item.id}`} sx={{ display: 'none', p: 2, bgcolor: '#f9f9f9', borderTop: '1px solid #eee' }}>
                        {item.type === 'procedure_execution' ? (
                            <Typography variant="body2">{item.description}</Typography>
                        ) : (
                            (item.medications || []).map((m, idx) => (
                                <Typography key={idx} variant="body2">• {m.commercialName} - {m.dosage}</Typography>
                            ))
                        )}
                    </Box>
                </Card>
            ))
        )}
      </Box>
    </Grid>
  </Grid>
</TabPanel>

{/* --- PESTAÑA 5: EVOLUCIONES RESTAURADA --- */}
<TabPanel value="5">
  <Grid container spacing={3}>
    <Grid item xs={12} md={5}>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fdfdfd' }}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MonitorHeartIcon /> {editingEvolutionId ? 'Editar Evolución' : 'Nueva Evolución'}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={6}><DatePicker label="Fecha" format="DD/MM/YYYY" value={newEvolution.date} onChange={(val) => setNewEvolution(p => ({ ...p, date: val }))} slotProps={{ textField: { size: 'small', fullWidth: true } }} /></Grid>
                <Grid item xs={6}><TextField label="Peso (Kg)" size="small" type="number" fullWidth value={newEvolution.weight || ""} onChange={(e) => setNewEvolution(p => ({...p, weight: e.target.value}))} /></Grid>
                <Grid item xs={6}>
                    <TextField 
                        label="Altura (cm)" 
                        size="small" 
                        type="number" 
                        fullWidth 
                        inputProps={{ step: 1 }} 
                        value={newEvolution.height || ""} 
                        onChange={(e) => setNewEvolution(p => ({...p, height: e.target.value}))} 
                    />
                </Grid>
                <Grid item xs={6}><TextField label="IMC" size="small" fullWidth value={newEvolution.bmi || ""} disabled InputProps={{startAdornment: <InputAdornment position="start">Calc</InputAdornment>}} /></Grid>
                <Grid item xs={6}><TextField label="TA Sistólica (mmHg)" size="small" type="number" fullWidth value={newEvolution.systolic || ""} onChange={(e) => setNewEvolution(p => ({...p, systolic: e.target.value}))} /></Grid>
                <Grid item xs={6}><TextField label="TA Diastólica (mmHg)" size="small" type="number" fullWidth value={newEvolution.diastolic || ""} onChange={(e) => setNewEvolution(p => ({...p, diastolic: e.target.value}))} /></Grid>
                <Grid item xs={6}><TextField label="Frec. Cardíaca" size="small" type="number" fullWidth value={newEvolution.heartRate || ""} onChange={(e) => setNewEvolution(p => ({...p, heartRate: e.target.value}))} /></Grid>
                <Grid item xs={6}><TextField label="Temp (°C)" size="small" type="number" fullWidth value={newEvolution.temperature || ""} onChange={(e) => setNewEvolution(p => ({...p, temperature: e.target.value}))} /></Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        select
                        label="Tipo de Consulta"
                        fullWidth
                        size="small"
                        value={newEvolution.visitType}
                        onChange={(e) => setNewEvolution(p => ({ ...p, visitType: e.target.value }))}
                    >
                        <MenuItem value="NUEVO">Primera Vez (Nuevo)</MenuItem>
                        <MenuItem value="SUCESIVO">Sucesiva (Control)</MenuItem>
                    </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        size="small"
                        options={specialties || []} 
                        value={newEvolution.specialty || ''}
                        onChange={(_, v) => setNewEvolution(p => ({...p, specialty: v}))}
                        renderInput={(params) => (
                            <TextField {...params} label="Especialidad de esta Consulta" />
                        )}
                        noOptionsText="No hay especialidades"
                    />
                </Grid>

                {/* --- FILA NUEVA: COBRO Y MOTIVO --- */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={availablePrices}
                    getOptionLabel={(option) => `${option.name} ($${option.amount})`}
                    value={availablePrices.find(p => p.id === newEvolution.servicePriceId) || null}
                    onChange={(_, newValue) => {
                        setNewEvolution(p => ({ 
                            ...p, 
                            servicePriceId: newValue ? newValue.id : '',
                            amountPaid: newValue ? newValue.amount : 0 
                        }));
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label="Buscar Servicio / Cobro" size="small" fullWidth />
                    )}
                    noOptionsText="No se encontró el servicio"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField 
                        label="Motivo de la consulta" 
                        placeholder="Ej: Control, Lectura de Labs"
                        size="small" 
                        fullWidth 
                        value={newEvolution.consultationReason || ""} 
                        onChange={(e) => setNewEvolution(p => ({...p, consultationReason: e.target.value}))} 
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField 
                      label="Nota Médica / Evolución" 
                      multiline rows={4} 
                      fullWidth 
                      value={newEvolution.evolutionText || ""} 
                      onChange={(e) => setNewEvolution(p => ({...p, evolutionText: e.target.value}))} 
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => handleOpenMacro('evolution')}><FlashOnIcon color="warning" /></IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button variant="contained" fullWidth onClick={handleSaveEvolution} startIcon={<SaveIcon />}>
                        {editingEvolutionId ? 'Actualizar Registro' : 'Registrar Evolución'}
                    </Button>
                    {editingEvolutionId && <Button fullWidth onClick={() => setEditingEvolutionId(null)} sx={{ mt: 1 }}>Cancelar</Button>}
                </Grid>
            </Grid>
        </Paper>
    </Grid>

    <Grid item xs={12} md={7}>
        {chartData.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, height: 320 }}>
                <Typography variant="subtitle2" gutterBottom align="center" fontWeight="bold">Curva de Evolución: Peso y Tensión Arterial</Typography>
                <ResponsiveContainer width="100%" height="90%">
                    <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Line type="monotone" dataKey="Peso" stroke="#8884d8" strokeWidth={3} activeDot={{ r: 8 }} />
                        <Line type="monotone" dataKey="Sistolica" stroke="#ff7300" strokeWidth={2} />
                        <Line type="monotone" dataKey="Diastolica" stroke="#82ca9d" strokeWidth={2} />
                    </LineChart>
                </ResponsiveContainer>
            </Paper>
        )}
        <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
            {(evolutionsList || []).map(evo => (
                <Card key={evo.id} sx={{ mb: 2, borderLeft: '5px solid #1976d2' }}>
                    <CardHeader 
                        title={dayjs(evo.date).format('DD/MM/YYYY')} 
                        subheader={evo.doctor ? `${evo.doctor.prefix || 'Dr.'} ${evo.doctor.name} ${evo.doctor.lastname}` : 'Médico Tratante'} 
                        action={
                            <Box>
                                {(isAdmin || evo.doctorId === user?.id) ? (
                                    <>
                                        <IconButton size="small" onClick={() => handleEditEvolution(evo)}><EditIcon /></IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDeleteEvolution(evo.id)}><DeleteIcon /></IconButton>
                                    </>
                                ) : (
                                    <Tooltip title="Solo lectura (Creado por otro médico)">
                                        <IconButton disabled>
                                            <LockIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        } 
                    />
                    <CardContent sx={{ pt: 0 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            {evo.weight && <Chip size="small" label={`Peso: ${evo.weight}kg`} color="primary" variant="outlined" />}
                            {evo.systolic && <Chip size="small" label={`TA: ${evo.systolic}/${evo.diastolic}`} color="error" variant="outlined" />}
                            {evo.bmi && <Chip size="small" label={`IMC: ${evo.bmi}`} color="success" variant="outlined" />}
                            {/* ✅ Mostramos el motivo en la tarjeta si existe */}
                            {evo.consultationReason && <Chip size="small" label={`Motivo: ${evo.consultationReason}`} variant="outlined" sx={{ fontWeight: 'bold' }} />}
                        </Box>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{evo.evolutionText}</Typography>
                    </CardContent>
                </Card>
            ))}
        </Box>
    </Grid>
  </Grid>
</TabPanel>
</TabContext>
 </Paper>
		
        <Dialog open={openDiagDialog} onClose={() => setOpenDiagDialog(false)} maxWidth="sm" fullWidth>
			<DialogTitle>Añadir Diagnóstico</DialogTitle>
			<DialogContent>
				<Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
				Buscar en CIE-10
				</Typography>
			<Autocomplete 
				options={cieOptions || []} 
				getOptionLabel={(o) => `${o.code} - ${o.description}`} 
				renderInput={(p) => <TextField {...p} label="Buscar por código o nombre" onChange={(e) => setCieInput(e.target.value)} />} 
				onChange={(_, n) => setSelectedDiag(n)} 
				loading={cieLoading} 
			/>
    
		<Divider sx={{ my: 2 }}>O</Divider>
    
			<Typography variant="subtitle2" gutterBottom>
				Diagnóstico Manual
			</Typography>
		<TextField 
			fullWidth
			label="Escriba el diagnóstico manualmente"
			placeholder="Ej: Hipertensión arterial esencial"
			value={manualDiag}
			onChange={(e) => setManualDiag(e.target.value)}
			helperText="Use este campo si no encuentra el diagnóstico en CIE-10"
		/>
	</DialogContent>
  <DialogActions>
    <Button onClick={() => setOpenDiagDialog(false)}>Cerrar</Button>
    <Button 
      onClick={handleAddDiagnosis} 
      variant="contained"
      disabled={!selectedDiag && !manualDiag.trim()}
    >
      Añadir
    </Button>
  </DialogActions>
</Dialog>
        
        <Dialog open={macroDialog.open} onClose={() => setMacroDialog({ open: false, targetField: null })} maxWidth="sm" fullWidth>
            <DialogTitle>{isCreatingMacro ? 'Nueva Plantilla' : 'Seleccionar Plantilla'} {!isCreatingMacro && <Button onClick={() => setIsCreatingMacro(true)}>Crear</Button>}</DialogTitle>
            <DialogContent dividers>
                {isCreatingMacro ? (
                    <Box><TextField label="Título" fullWidth value={newMacroForm.title} onChange={(e) => setNewMacroForm({...newMacroForm, title: e.target.value})} sx={{ mb: 2 }} /><TextField label="Contenido" fullWidth multiline rows={4} value={newMacroForm.content} onChange={(e) => setNewMacroForm({...newMacroForm, content: e.target.value})} /></Box>
                ) : (
                    <List>{(filteredMacros || []).map((m) => (<ListItem key={m.id} onClick={() => handleSelectMacro(m.content)} sx={{ cursor: 'pointer' }}><ListItemText primary={m.title} secondary={(m.content || "").length > 50 ? m.content.substring(0, 50) + '...' : m.content} /><IconButton onClick={(e) => handleDeleteMacro(m.id, e)} color="error"><DeleteIcon /></IconButton></ListItem>))}</List>
                )}
            </DialogContent>
            <DialogActions>{isCreatingMacro && <Button onClick={handleCreateMacro}>Guardar</Button>}<Button onClick={() => setMacroDialog({ open: false, targetField: null })}>Cerrar</Button></DialogActions>
        </Dialog>

<Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white' }}>
        Vincular Resultados de Laboratorio
    </DialogTitle>
    <DialogContent dividers>
        <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
            Seleccione el lote de resultados que desea adjuntar a esta reseña física:
        </Typography>

        <List>
            {/* OPCIÓN: NO VINCULAR */}
            <ListItemButton 
                selected={selectedLabBatchIndex === -1}
                onClick={() => setSelectedLabBatchIndex(-1)}
                sx={{ borderRadius: 1, mb: 1, border: '1px solid #ddd' }}
            >
                <ListItemIcon><BlockIcon color="error" /></ListItemIcon>
                <ListItemText primary="No vincular laboratorios" secondary="Guardar solo la reseña escrita" />
                <Checkbox checked={selectedLabBatchIndex === -1} />
            </ListItemButton>

            <Divider sx={{ my: 1 }}>Resultados en sistema</Divider>

            {/* LISTA DE LOTES REALES */}
            {paraclinicalsList.map((lote, idx) => (
                <ListItemButton 
                    key={idx}
                    selected={selectedLabBatchIndex === idx}
                    onClick={() => setSelectedLabBatchIndex(idx)}
                    sx={{ 
                        borderRadius: 1, 
                        mb: 1, 
                        border: selectedLabBatchIndex === idx ? '2px solid #2e7d32' : '1px solid #ddd',
                        bgcolor: selectedLabBatchIndex === idx ? '#f1f8e9' : 'transparent'
                    }}
                >
                    <ListItemIcon><BiotechIcon color="primary" /></ListItemIcon>
                    <ListItemText 
                        primary={`Laboratorio del día: ${lote.fecha_ingreso || lote.fecha}`} 
                        secondary={`${Object.keys(lote).length - 2} exámenes encontrados`} 
                    />
                    <Checkbox checked={selectedLabBatchIndex === idx} color="success" />
                </ListItemButton>
            ))}
        </List>

        {paraclinicalsList.length === 0 && (
            <Alert severity="warning">No hay laboratorios previos cargados para este paciente.</Alert>
        )}
    </DialogContent>
    <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Button onClick={() => loadParaclinicals(true)} startIcon={<FlashOnIcon />}>
            Actualizar Lista
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => setOpenConfirmDialog(false)}>Cancelar</Button>
        <Button 
            variant="contained" 
            color="success" 
            onClick={processPhysicalAndSave}
            disabled={medicalHistory.physicalExam.description?.length < 3}
        >
            Confirmar y Guardar
        </Button>
    </DialogActions>
</Dialog>

      </Box>
    </LocalizationProvider>
  );
}

export default MedicalHistoryPage;
